import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  Button,
  Chip,
  SessionBoxInfo,
  SquareButton,
  Toggle,
  useBoolModalState,
  useToast,
  TextArea,
} from 'components';
import {
  Incentive,
  useIncentiveForSession,
  usePlan,
  useSession,
  useTranslateIncentiveText,
  useUpdatePlan,
} from '@app/api';
import { AdminIncentiveEdition } from './AdminIncentiveEdition';

const useStyles = createUseStyles((theme: Theme) => ({
  toggleContent: {
    display: 'flex',
    gap: theme.marginBase,
    alignItems: 'center',
  },
  field: {
    marginTop: theme.marginBase * 2,
  },
  label: {
    ...theme.fonts.label,
  },
  line: {
    display: 'flex',
    gap: theme.marginBase,
  },
  incentive: {
    ...theme.fonts.caption,
    flex: 1,
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase,
    borderRadius: theme.borderRadius.std,
  },
  referralText: {
    ...theme.fonts.caption,
    flex: 1,
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase,
    borderRadius: theme.borderRadius.std,
    overflow: 'hidden',

    whiteSpace: 'pre-wrap',
  },
  referralTextButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 2,
  },
}));

interface Props {
  className?: string;
  sessionId?: string;
  recruitmentId?: string;
}

export const ReferralCard = ({ className, sessionId }: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const [isTextEditting, editText, stopEditting] = useBoolModalState();
  const [isEdittingIncentive, openEditIncentive, stopEdittingIncentive] =
    useBoolModalState();
  const { data: session } = useSession(sessionId);
  const { mutate: updatePlan } = useUpdatePlan(session?.planId);
  const { data: plan } = usePlan(session?.planId);
  const [initialIncentive, setIncentive] = useState<Incentive | undefined>();

  const { data: originalIncentive } = useIncentiveForSession(session?.id);

  const hasIncentive = !!plan?.referralIncentive;

  const [referralTextState, setReferralText] = useState<string | undefined>(
    undefined,
  );
  const referralText =
    referralTextState !== undefined ? referralTextState : plan?.referralText;

  const generateDefaultIncentive = useCallback(() => {
    if (hasIncentive) {
      return;
    }
    const newIncentive = originalIncentive
      ? {
          ...originalIncentive,
          amount: (originalIncentive?.amount || 0) / 2,
        }
      : { amount: 0, currencyCode: 'EUR' };

    setIncentive(newIncentive);
    updatePlan(
      {
        referralActivated: true,
        referralIncentive: newIncentive,
      },
      {
        onSuccess: () => toast.saved(),
        onError: (e) => toast.exception(e),
      },
    );
  }, [hasIncentive, setIncentive, plan, toast, updatePlan, originalIncentive]);

  const onToggleReferral = useCallback(
    (checked: boolean) => {
      if (checked && !hasIncentive) {
        generateDefaultIncentive();
        return;
      }
      updatePlan(
        {
          referralActivated: checked,
        },
        {
          onSuccess: () => toast.saved(),
          onError: (e) => toast.exception(e),
        },
      );
    },
    [updatePlan, toast, generateDefaultIncentive, hasIncentive],
  );

  const editIncentive = useCallback(() => {
    generateDefaultIncentive();
    openEditIncentive();
  }, [generateDefaultIncentive, openEditIncentive]);

  const updateReferralInfos = useCallback(() => {
    updatePlan(
      { referralText },
      {
        onSuccess: () => stopEditting(),
        onError: (e) => toast.exception(e),
      },
    );
  }, [referralText, updatePlan]);

  const saveIncentive = useCallback(
    (incentive: Incentive) => {
      updatePlan(
        {
          referralIncentive: incentive,
        },
        {
          onSuccess: () => {
            stopEdittingIncentive();
            toast.saved();
          },
          onError: toast.exception,
        },
      );
    },
    [stopEdittingIncentive, toast],
  );

  const incentiveText = useTranslateIncentiveText(plan?.referralIncentive);

  return (
    <SessionBoxInfo
      className={className}
      color="green"
      title="Referral"
      icon="ri-share-box-line"
      allowOverflow
    >
      <Toggle onChange={onToggleReferral} checked={plan?.referralActivated}>
        <div className={classes.toggleContent}>
          <div className={classes.label}>Status du parainaige</div>
          {plan?.referralActivated ? (
            <Chip color="green">ACTIF</Chip>
          ) : (
            <Chip color="red">INACTIF</Chip>
          )}
        </div>
      </Toggle>
      {isEdittingIncentive ? (
        <AdminIncentiveEdition
          onSubmit={saveIncentive}
          incentive={initialIncentive || plan?.referralIncentive}
        />
      ) : (
        <>
          <div className={classes.field}>
            <label className={classes.label} htmlFor="referral-incentiveText">
              Contrepartie pour le parrain
            </label>
          </div>
          <div className={classes.line}>
            <p id="referral-incentiveText" className={classes.incentive}>
              {incentiveText || '/'}
            </p>
            <SquareButton
              color="green"
              icon="ri-edit-line"
              onClick={editIncentive}
            />
          </div>
        </>
      )}
      <div className={classes.field}>
        <label className={classes.label} htmlFor="referralText">
          Description de la cible pour les participants
        </label>
      </div>
      {isTextEditting ? (
        <div>
          <TextArea
            value={referralText}
            rows={10}
            noMargin
            onChange={(e) => setReferralText(e.currentTarget.value)}
          />
          <div className={classes.referralTextButtons}>
            <Button small type="button" onClick={updateReferralInfos}>
              Sauvegarder
            </Button>
          </div>
        </div>
      ) : (
        <div className={classes.line}>
          <div id="referralText" className={classes.referralText}>
            {referralText}
          </div>
          <SquareButton color="green" icon="ri-edit-line" onClick={editText} />
        </div>
      )}
    </SessionBoxInfo>
  );
};
